import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CrisisFooter } from '@/components/CrisisFooter';
import {
  JournalStats,
  SearchBar,
  FilterPanel,
  JournalEntryCard,
  Pagination,
  EntryModal,
  EmptyState
} from '@/components/journal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JournalPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total_entries: 0, total_words: 0, unique_days: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total_entries: 0, total_pages: 1 });
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPhase, setSelectedPhase] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Selected entry modal
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchEntries = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '10' });
      if (searchQuery) params.append('search', searchQuery);
      if (selectedPhase) params.append('phase', selectedPhase);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API}/journal/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEntries(response.data.entries);
      setPagination(response.data.pagination);
      setStats(response.data.stats);
    } catch {
      toast.error('Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, selectedPhase, dateFrom, dateTo]);

  useEffect(() => {
    fetchEntries(1);
  }, [fetchEntries]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEntries(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPhase('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry? This cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/journal/entry/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      setSelectedEntry(null);
      fetchEntries(pagination.page);
    } catch {
      toast.error('Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportEntry = async (entryId) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/journal/export-pdf?entry_id=${entryId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `journal_entry_${entryId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Entry exported!');
    } catch {
      toast.error('Failed to export entry');
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/journal/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mindful_renewal_journal_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Journal exported!');
    } catch {
      toast.error('Failed to export journal');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{ 
        background: 'linear-gradient(180deg, #FBF9F6 0%, #F7F1EE 50%, #FBF9F6 100%)',
        fontFamily: 'Jost, sans-serif'
      }}
    >
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b" style={{ borderColor: '#F7F1EE', background: 'rgba(251,249,246,0.8)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/sanctuary')}
              className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
              style={{ color: '#4A4A5A' }}
              data-testid="journal-back-button"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sanctuary
            </button>
            
            <Button
              onClick={handleExportAll}
              disabled={exporting || stats.total_entries === 0}
              variant="outline"
              size="sm"
              className="rounded-full border-[#EAE4DC] text-[#4A4A5A] hover:bg-[#F7F1EE]"
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-medium tracking-tight mb-2"
            style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}
            data-testid="journal-heading"
          >
            My Journal
          </h1>
          <p style={{ color: '#6B6878' }}>
            Your reflections and insights from the journey
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" style={{ color: '#C9A84C' }} />
              <span style={{ color: '#4A4A5A' }}>
                <span className="font-medium" style={{ color: '#C9A84C' }}>{stats.total_entries}</span> entries
              </span>
            </div>
            <span style={{ color: '#A3A0AE' }}>•</span>
            <span style={{ color: '#6B6878' }}>
              {stats.total_words?.toLocaleString() || 0} words
            </span>
            <span style={{ color: '#A3A0AE' }}>•</span>
            <span style={{ color: '#6B6878' }}>
              {stats.unique_days} days reflected
            </span>
          </div>
        </div>

        {/* Search & Filters */}
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          onSubmit={handleSearch}
          onToggleFilters={() => setShowFilters(!showFilters)}
        />
        
        <FilterPanel
          show={showFilters}
          selectedPhase={selectedPhase}
          onPhaseChange={setSelectedPhase}
          dateFrom={dateFrom}
          onDateFromChange={setDateFrom}
          dateTo={dateTo}
          onDateToChange={setDateTo}
          onClear={clearFilters}
        />

        {/* Entries List */}
        {(() => {
          if (loading) {
            return (
              <div className="flex justify-center py-12">
                <div className="animate-pulse text-[#6B6878]">Loading entries...</div>
              </div>
            );
          }
          if (entries.length === 0) {
            return <EmptyState />;
          }
          return (
            <div className="space-y-3">
              {entries.map((entry) => (
                <JournalEntryCard
                  key={entry.id}
                  entry={entry}
                  onClick={() => setSelectedEntry(entry)}
                />
              ))}
            </div>
          );
        })()}

        {/* Pagination */}
        <Pagination
          current={pagination.page}
          total={pagination.total_pages}
          onPageChange={(page) => fetchEntries(page)}
        />
      </main>

      {/* Entry Detail Modal */}
      <EntryModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onDelete={handleDeleteEntry}
        onExport={handleExportEntry}
        deleting={deleting}
        exporting={exporting}
      />

      <CrisisFooter />
    </div>
  );
};

export default JournalPage;
